import cn from "classnames"
import React, { useEffect, useRef } from "react"
import { isTablet } from "@/common/util/misc"

interface ScrollableProps {
  /** Additional class name for wrapper element */
  className?: string
  /** Scroller direction is horizontal */
  horizontal?: boolean
  /** Scroll to active element on mount */
  scrollToActive?: boolean
  /** Optional CSS selector to find active element to scroll to */
  activeSelector?: string
  /** Delay scrolling to active to account for toolbox animations */
  scrollActiveToolboxDelay?: boolean

  children?: React.ReactNode
}

const Scrollable: React.FC<ScrollableProps> = ({
  className,
  scrollToActive,
  activeSelector,
  scrollActiveToolboxDelay,
  children,
  horizontal,
}) => {
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const box = boxRef.current

    if (box && scrollToActive) {
      const selector = activeSelector || ".active"
      const active = box.querySelector<HTMLDivElement>(`.scrollable-children-inner > ${selector}`)

      if (active) {
        const animation = 300 / 20
        const select = () => {
          try {
            active.scrollIntoView({ block: "center" })
          } catch {
            active.scrollIntoView(false)
          }
        }

        scrollActiveToolboxDelay && isTablet() && animation > 0 ? setTimeout(() => select(), animation / 20) : select()
      }
    }
  }, [scrollToActive, activeSelector, scrollActiveToolboxDelay])

  return (
    <div
      className={cn("scrollable relative h-full overflow-hidden", className, {
        horizontal: horizontal,
        "vertical w-full": !horizontal,
      })}
    >
      <div
        className={cn("scrollable-outer absolute bottom-0 left-0 right-0 top-0", {
          "overflow-x-auto overflow-y-hidden": horizontal,
          "overflow-x-hidden overflow-y-scroll": !horizontal,
        })}
        ref={boxRef}
      >
        <div
          className={cn("scrollable-children-inner w-full transform-gpu", {
            "translate-y-0": horizontal,
            "translate-x-0": !horizontal,
          })}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

export default Scrollable
